import React,{useState,useEffect,useCallback}from'react';
import{useNavigate,useParams,useSearchParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';

export default function IPDDischargePage(){
  const nav=useNavigate();
  const {admissionId}=useParams();
  const [loading,setLoading]=useState(true);
  const [sp]=useSearchParams();
  const aid=admissionId||sp.get('admissionId')||'';
  const [admission,setAdmission]=useState<any>(null);
  const [bill,setBill]=useState<any>(null);
  const [checklist,setChecklist]=useState<any[]>([{item:'Pending lab results reviewed',done:false},{item:'Pending radiology reports reviewed',done:false},{item:'Discharge summary prepared',done:false},{item:'Patient/family counselled',done:false},{item:'Discharge medications prescribed',done:false},{item:'Follow-up appointment scheduled',done:false},{item:'Patient signature on discharge summary',done:false},{item:'Final bill settled',done:false}]);
  const [form,setForm]=useState({dischargeType:'REGULAR',finalDiagnosis:'',finalCondition:'STABLE',dischargeNotes:'',followUpDate:'',followUpDoctor:'',followUpInstructions:'',dischargeMedications:'',dietAdvice:'',activityRestrictions:''});
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  const load=useCallback(async()=>{
    try{
      setLoading(true);const[a,b]=await Promise.all([
        aid?api.getAdmissionById?.(aid).catch(()=>({data:null})):Promise.resolve({data:null}),
        aid?api.getAdmissionBill?.(aid).catch(()=>({data:null})):Promise.resolve({data:null}),
      ]);
      setAdmission(a?.data);setBill(b?.data);
    }catch{}
    setLoading(false);
  },[aid]);
  useEffect(()=>{load();},[load]);

  const allChecked=checklist.every(c=>c.done);
  const checkDone=(i:number)=>setChecklist(c=>c.map((x,j)=>j===i?{...x,done:!x.done}:x));

  const submit=async()=>{
    if(!allChecked){setMsg('Please complete all checklist items before discharge');return;}
    if(!form.finalDiagnosis){setMsg('Final diagnosis is required');return;}
    setSaving(true);
    try{
      await api.dischargePatient?.(aid,form);
      nav('/ipd');
    }catch(e:any){setMsg('Error: '+(e.response?.data?.message||'Discharge failed'));}
    setSaving(false);
  };

  const DISCHARGE_TYPES=[{v:'REGULAR',l:'Regular Discharge',color:'green'},{v:'LAMA',l:'LAMA (Against Medical Advice)',color:'orange'},{v:'DAMA',l:'DAMA (Discharge Against MA)',color:'orange'},{v:'REFERRED',l:'Referred to Another Facility',color:'blue'},{v:'ABSCONDED',l:'Absconded',color:'red'},{v:'EXPIRED',l:'Expired',color:'gray'}];

  return(
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">Discharge Patient</h1><p className="text-gray-500">Complete discharge process and generate summary</p></div>
          <button onClick={()=>nav('/ipd')} className="border px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">← IPD</button>
        </div>
        {msg&&<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{msg}</div>}

        {admission&&(
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div><p className="text-gray-500">Patient</p><p className="font-bold text-blue-800">{admission.patient_name||admission.patientName||'—'}</p></div>
              <div><p className="text-gray-500">Admission No.</p><p className="font-semibold">{admission.admission_number||admission.id?.substring(0,8)||'—'}</p></div>
              <div><p className="text-gray-500">Admitted</p><p className="font-semibold">{admission.admission_date?.split('T')[0]||'—'}</p></div>
              <div><p className="text-gray-500">Ward/Bed</p><p className="font-semibold">{admission.ward_name||'—'} / Bed {admission.bed_number||'—'}</p></div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            {/* Discharge Type */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-bold text-gray-800 mb-4">Discharge Type</h3>
              <div className="grid grid-cols-3 gap-2">
                {DISCHARGE_TYPES.map(t=>(
                  <button key={t.v} onClick={()=>f('dischargeType',t.v)} className={`p-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${form.dischargeType===t.v?`border-${t.color}-500 bg-${t.color}-50`:'border-gray-200 hover:border-gray-300'}`}>{t.l}</button>
                ))}
              </div>
            </div>

            {/* Clinical Summary */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-bold text-gray-800 mb-4">Discharge Summary</h3>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">Final Diagnosis *</label><input value={form.finalDiagnosis} onChange={e=>f('finalDiagnosis',e.target.value)} className="w-full border rounded-lg p-2" placeholder="Primary diagnosis at discharge"/></div>
                <div><label className="block text-sm font-medium mb-1">Condition at Discharge</label><select value={form.finalCondition} onChange={e=>f('finalCondition',e.target.value)} className="w-full border rounded-lg p-2">{['STABLE','IMPROVED','SAME','DETERIORATED','CRITICAL','EXPIRED'].map(c=><option key={c}>{c}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Clinical Notes</label><textarea value={form.dischargeNotes} onChange={e=>f('dischargeNotes',e.target.value)} className="w-full border rounded-lg p-2" rows={4} placeholder="Hospital course, treatment given, procedures performed…"/></div>
                <div><label className="block text-sm font-medium mb-1">Discharge Medications</label><textarea value={form.dischargeMedications} onChange={e=>f('dischargeMedications',e.target.value)} className="w-full border rounded-lg p-2" rows={3} placeholder="Tab. Amlodipine 5mg OD × 30 days&#10;Tab. Metformin 500mg BD × 30 days"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium mb-1">Diet Advice</label><textarea value={form.dietAdvice} onChange={e=>f('dietAdvice',e.target.value)} className="w-full border rounded-lg p-2" rows={2} placeholder="Low salt, diabetic diet…"/></div>
                  <div><label className="block text-sm font-medium mb-1">Activity Restrictions</label><textarea value={form.activityRestrictions} onChange={e=>f('activityRestrictions',e.target.value)} className="w-full border rounded-lg p-2" rows={2} placeholder="Bed rest for 2 weeks…"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium mb-1">Follow-up Date</label><input type="date" value={form.followUpDate} onChange={e=>f('followUpDate',e.target.value)} className="w-full border rounded-lg p-2"/></div>
                  <div><label className="block text-sm font-medium mb-1">Follow-up Doctor/Dept</label><input value={form.followUpDoctor} onChange={e=>f('followUpDoctor',e.target.value)} className="w-full border rounded-lg p-2" placeholder="Dr. Sharma / Cardiology"/></div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Discharge Checklist */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-bold text-gray-800 mb-3">Discharge Checklist</h3>
              <div className="space-y-2">
                {checklist.map((c,i)=>(
                  <label key={i} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer ${c.done?'bg-green-50':'hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={c.done} onChange={()=>checkDone(i)} className="w-4 h-4 mt-0.5 rounded"/>
                    <span className={`text-sm ${c.done?'text-green-700 line-through':''}`}>{c.item}</span>
                  </label>
                ))}
              </div>
              <div className={`mt-3 p-3 rounded-lg text-sm font-semibold ${allChecked?'bg-green-100 text-green-700':'bg-amber-50 text-amber-700'}`}>
                {allChecked?'✅ Checklist complete':'⚠️ '+checklist.filter(c=>!c.done).length+' items pending'}
              </div>
            </div>

            {/* Bill Summary */}
            {bill&&(
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-bold text-gray-800 mb-3">Bill Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Total Amount</span><span className="font-bold">₹{(bill.total_amount||bill.totalAmount||0).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Paid (Advance)</span><span className="text-green-600">₹{(bill.paid_amount||bill.paidAmount||0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-red-600 font-bold border-t pt-2"><span>Balance Due</span><span>₹{((bill.total_amount||0)-(bill.paid_amount||0)).toLocaleString()}</span></div>
                </div>
                {(bill.total_amount||bill.totalAmount||0)>(bill.paid_amount||bill.paidAmount||0)&&(
                  <button type="button" onClick={()=>{
                    const pid=admission?.patientId||admission?.patient_id||bill.patientId||bill.patient_id;
                    const bid=bill.id||bill.billId;
                    if(bid) nav(`/billing/bill/${bid}`);
                    else nav('/billing',{state:{patientId:pid,admissionId:aid}});
                  }} className="w-full mt-3 bg-red-600 text-white py-2 rounded-lg text-sm hover:bg-red-700">Collect Payment ₹{(((bill.total_amount||bill.totalAmount||0)-(bill.paid_amount||bill.paidAmount||0))).toLocaleString()}</button>
                )}
              </div>
            )}

            <button onClick={submit} disabled={saving||!allChecked} className="w-full py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:opacity-50 text-lg">
              {saving?'Processing…':'✓ Complete Discharge'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
